import { useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LoadingButton from '@mui/lab/LoadingButton';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../../contexts/strings';
import BaseDialog from '../../../../common/base-dialog';
import { useCreateSegmentFromPoll } from '../../../../../hooks/useSegments';
import { PollAudience, WinnerPick } from '../../../../../types/segment';
import { Poll } from '../../types';
import { getStatus } from '../../data';
import { SxStyle } from '../../../../../types';

/** Parses the number of winners typed by the admin; `undefined` unless it is a whole number of at least 1. */
const parseLimit = (value: string): number | undefined => {
  const limit = Number(value);
  return value.trim() !== '' && Number.isInteger(limit) && limit >= 1 ? limit : undefined;
};

const styles: SxStyle = {
  controlLabel: {
    mb: 1,
  },
  // Same height as a small TextField
  toggleGroup: {
    height: 40,
  },
};

interface Props {
  open: boolean;
  selectedPoll: Poll & { title?: string };
  onClose: () => void;
}

const PollPushSegmentModal = ({ open, selectedPoll, onClose }: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const { mutateAsync, isPending } = useCreateSegmentFromPoll(selectedPoll.abbrv);

  const isQuiz = selectedPoll.type === 'quiz';
  const isFinished = getStatus(selectedPoll) === 'FINISHED';
  const pollTitle = selectedPoll.title || selectedPoll.name;
  // ISO date, since the name is part of the segment's Firestore id, where "/" is not allowed
  const pollDate = selectedPoll.startTime
    ? moment(selectedPoll.startTime).format('YYYY-MM-DD')
    : '';

  const getDefaultName = (value: PollAudience) =>
    `${pollDate ? `${pollDate} ` : ''}${pollTitle} – ${
      value === 'WINNERS'
        ? strings.polls.pushGroupNameWinners
        : strings.polls.pushGroupNameParticipants
    }`;

  const [audience, setAudience] = useState<PollAudience>('WINNERS');
  const [limitWinners, setLimitWinners] = useState(false);
  const [limitInput, setLimitInput] = useState('1');
  const [pick, setPick] = useState<WinnerPick>('RANDOM');
  const [name, setName] = useState(getDefaultName('WINNERS'));
  // Prefilled, since the segment form requires a description when the segment is edited later
  const [description, setDescription] = useState(
    strings.polls.pushGroupDefaultDescription.replace('$title', pollTitle)
  );

  const onAudienceChange = (value: PollAudience) => {
    // Keep a name the admin typed, but follow the audience while it is the default
    if (name === getDefaultName(audience)) {
      setName(getDefaultName(value));
    }
    setAudience(value);
  };

  const isLimited = audience === 'WINNERS' && limitWinners;
  const limit = parseLimit(limitInput);
  const isLimitInvalid = isLimited && limit === undefined;

  const onCreate = async () => {
    try {
      const result = await mutateAsync({
        pollId: selectedPoll.id,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        audience,
        ...(isLimited && { limit, pick }),
      });

      enqueueSnackbar(strings.polls.pushGroupCreated.replace('$count', String(result.idsCount)), {
        variant: 'success',
        action: (key) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(key);
              history.push(`/pushdashboard/${selectedPoll.abbrv}/segments`);
            }}
          >
            {strings.polls.pushGroupOpenSegments}
          </Button>
        ),
      });
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error instanceof Error ? error.message : strings.polls.pushGroupError, {
        variant: 'error',
      });
    }
  };

  return (
    <BaseDialog open={open} title={strings.polls.pushGroupTitle} onClose={onClose}>
      <DialogContent>
        <Stack gap={3} sx={{ pt: 2 }}>
          {!isFinished && <Alert severity="warning">{strings.polls.pushGroupOngoingWarning}</Alert>}

          <FormControl>
            <FormLabel>{strings.polls.pushGroupAudienceLabel}</FormLabel>
            <RadioGroup
              value={audience}
              onChange={(event) => onAudienceChange(event.target.value as PollAudience)}
            >
              <FormControlLabel
                value="WINNERS"
                control={<Radio />}
                label={
                  isQuiz
                    ? strings.polls.pushGroupAudienceWinnersQuiz
                    : strings.polls.pushGroupAudienceWinners
                }
              />
              <FormControlLabel
                value="PARTICIPANTS"
                control={<Radio />}
                label={strings.polls.pushGroupAudienceParticipants}
              />
            </RadioGroup>
          </FormControl>

          {audience === 'WINNERS' && (
            <Stack gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={limitWinners}
                    onChange={(event) => setLimitWinners(event.target.checked)}
                  />
                }
                label={strings.polls.pushGroupLimitWinners}
              />
              {limitWinners && (
                <Stack direction="row" gap={4} alignItems="flex-start">
                  {/* Both controls have their label above them, so the label rows and the
                      40px high controls line up */}
                  <FormControl>
                    <FormLabel htmlFor="push-group-limit" sx={styles.controlLabel}>
                      {strings.polls.pushGroupLimitLabel}
                    </FormLabel>
                    <TextField
                      id="push-group-limit"
                      type="number"
                      size="small"
                      value={limitInput}
                      onChange={(event) => setLimitInput(event.target.value)}
                      inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
                      error={isLimitInvalid}
                      helperText={isLimitInvalid ? strings.polls.pushGroupLimitInvalid : undefined}
                      sx={{ width: 160 }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel sx={styles.controlLabel}>
                      {strings.polls.pushGroupPickLabel}
                    </FormLabel>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      sx={styles.toggleGroup}
                      value={pick}
                      onChange={(_, value: WinnerPick | null) => value && setPick(value)}
                    >
                      <ToggleButton value="RANDOM">
                        {strings.polls.pushGroupPickRandom}
                      </ToggleButton>
                      <ToggleButton value="EARLIEST">
                        {strings.polls.pushGroupPickEarliest}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </Stack>
              )}
            </Stack>
          )}

          <TextField
            label={strings.polls.pushGroupNameLabel}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 100 }}
            fullWidth
          />
          <TextField
            label={strings.polls.pushGroupDescriptionLabel}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            inputProps={{ maxLength: 500 }}
            multiline
            rows={3}
            fullWidth
          />

          <Alert severity="info">{strings.polls.pushGroupSnapshotInfo}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={(theme) => ({ borderTop: `1px solid ${theme.palette.grey[300]}` })}>
        <Button color="primary" onClick={onClose}>
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isPending}
          startIcon={<GroupAddIcon />}
          onClick={onCreate}
          disabled={!name.trim() || isLimitInvalid}
        >
          {strings.polls.pushGroupCreate}
        </LoadingButton>
      </DialogActions>
    </BaseDialog>
  );
};

export default PollPushSegmentModal;
